import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent } from 'react'
import './DragDropMatchingGame.css'
import { type CrucialReactProps } from './crucialReactMount'
import { setHostTheme, type ThemeMode } from './themeHandler'
import puzzleIcon from './assets/puzzle.svg'

export type DragDropPair = {
  id: string
  term: string
  match: string
}

// Config for Drag and Drop React Config
export type DragDropMatchingConfig = CrucialReactProps & {
  pairs: DragDropPair[]
  roundSize?: number
  termsTitle: string
  definitionsTitle: string
  showStatCard: boolean
  scoreLabel: string
  shuffleLabel: string
  chipMatchedLabel: string
  chipSelectedLabel: string
  chipDragLabel: string
  targetTapLabel: string
  targetDropLabel: string
  feedbackCorrect: string
  feedbackIncorrect: string
  progressAriaLabel: string
}

type DragDropMatchingGameProps = {
  config: DragDropMatchingConfig
  theme?: ThemeMode
}

const createMatchState = (pairs: DragDropPair[]) =>
  Object.fromEntries(pairs.map((pair) => [pair.id, null])) as Record<string, string | null>

const normalizeRoundSize = (pairs: DragDropPair[], roundSize?: number) => {
  if (pairs.length === 0) return 0
  const requested = Number.isFinite(roundSize) ? Math.floor(roundSize ?? 0) : pairs.length
  return Math.min(Math.max(requested, 1), pairs.length)
}

const shuffle = <T,>(items: T[]) => {
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

const pickRoundPairs = (pairs: DragDropPair[], roundSize: number, seenIds: Set<string>) => {
  if (roundSize === 0) return []
  const unseen = pairs.filter((pair) => !seenIds.has(pair.id))
  const selected = shuffle(unseen).slice(0, roundSize)
  if (selected.length < roundSize) {
    const remaining = pairs.filter((pair) => !selected.some((item) => item.id === pair.id))
    selected.push(...shuffle(remaining).slice(0, roundSize - selected.length))
  }
  return selected
}

const pickUnseenPairs = (pairs: DragDropPair[], roundSize: number, seenIds: Set<string>) => {
  if (roundSize === 0) return []
  return shuffle(pairs.filter((pair) => !seenIds.has(pair.id))).slice(0, roundSize)
}

const formatTemplate = (template: string, values: Record<string, number | string>) =>
  template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''))

export default function DragDropMatchingGame({ config, theme }: DragDropMatchingGameProps) {
  const poolPairs = config.pairs
  const activeTheme = theme ?? 'dark'
  const [isOpen, setIsOpen] = useState(false)
  const roundSize = normalizeRoundSize(poolPairs, config.roundSize)
  const seenIdsRef = useRef<Set<string>>(new Set())
  const [roundPairs, setRoundPairs] = useState<DragDropPair[]>(() => {
    const initialPairs = pickRoundPairs(poolPairs, roundSize, seenIdsRef.current)
    initialPairs.forEach((pair) => seenIdsRef.current.add(pair.id))
    return initialPairs
  })
  const [terms, setTerms] = useState(() => shuffle(roundPairs))
  const [targets, setTargets] = useState(() => shuffle(roundPairs))
  const [matches, setMatches] = useState(() => createMatchState(roundPairs))
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [activeTargetId, setActiveTargetId] = useState<string | null>(null)
  const [wrongTargetId, setWrongTargetId] = useState<string | null>(null)
  const [draggingTermId, setDraggingTermId] = useState<string | null>(null)
  const [isFinished, setIsFinished] = useState(false)
  const [fadingTermIds, setFadingTermIds] = useState<Set<string>>(new Set())
  const [fadingTargetIds, setFadingTargetIds] = useState<Set<string>>(new Set())
  const [hiddenTermIds, setHiddenTermIds] = useState<Set<string>>(new Set())
  const [hiddenTargetIds, setHiddenTargetIds] = useState<Set<string>>(new Set())
  const pairLookup = useMemo(
    () => new Map(poolPairs.map((pair) => [pair.id, pair])),
    [poolPairs],
  )

  const matchedIds = useMemo(
    () => Object.values(matches).filter((value): value is string => Boolean(value)),
    [matches],
  )
  const matchedTermIds = useMemo(() => new Set(matchedIds), [matchedIds])
  const matchedCount = matchedIds.length
  const totalPairs = roundPairs.length
  const progress = totalPairs ? matchedCount / totalPairs : 0
  const isComplete = totalPairs > 0 && matchedCount === totalPairs
  const fadeDelayMs = 700
  const fadeDurationMs = 320

  useEffect(() => {
    if (!feedback) return
    const timeoutId = window.setTimeout(() => setFeedback(null), 1600)
    return () => window.clearTimeout(timeoutId)
  }, [feedback])

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen])

  useEffect(() => {
    if (!wrongTargetId) return
    const timeoutId = window.setTimeout(() => setWrongTargetId(null), 600)
    return () => window.clearTimeout(timeoutId)
  }, [wrongTargetId])

  const applyRound = (nextPairs: DragDropPair[]) => {
    setRoundPairs(nextPairs)
    setTerms(shuffle(nextPairs))
    setTargets(shuffle(nextPairs))
    setMatches(createMatchState(nextPairs))
    setSelectedTermId(null)
    setFeedback(null)
    setActiveTargetId(null)
    setWrongTargetId(null)
    setDraggingTermId(null)
    setFadingTermIds(new Set())
    setFadingTargetIds(new Set())
    setHiddenTermIds(new Set())
    setHiddenTargetIds(new Set())
    setIsFinished(false)
  }

  const startNewRound = () => {
    const nextPairs = pickRoundPairs(poolPairs, roundSize, seenIdsRef.current)
    nextPairs.forEach((pair) => seenIdsRef.current.add(pair.id))
    applyRound(nextPairs)
  }

  const startFresh = () => {
    seenIdsRef.current = new Set()
    const nextPairs = pickRoundPairs(poolPairs, roundSize, seenIdsRef.current)
    nextPairs.forEach((pair) => seenIdsRef.current.add(pair.id))
    applyRound(nextPairs)
  }

  useEffect(() => {
    startFresh()
  }, [poolPairs, roundSize])

  const attemptMatch = (termId: string, targetId: string) => {
    if (matches[targetId]) return
    if (matchedTermIds.has(termId)) return

    if (termId === targetId) {
      setMatches((prev) => ({ ...prev, [targetId]: termId }))
      setFeedback(config.feedbackCorrect)
      window.setTimeout(() => {
        setFadingTermIds((prev) => new Set(prev).add(termId))
        setFadingTargetIds((prev) => new Set(prev).add(targetId))
      }, fadeDelayMs)
      window.setTimeout(() => {
        setHiddenTermIds((prev) => new Set(prev).add(termId))
        setHiddenTargetIds((prev) => new Set(prev).add(targetId))
        setFadingTermIds((prev) => {
          const next = new Set(prev)
          next.delete(termId)
          return next
        })
        setFadingTargetIds((prev) => {
          const next = new Set(prev)
          next.delete(targetId)
          return next
        })
      }, fadeDelayMs + fadeDurationMs)
    } else {
      setFeedback(config.feedbackIncorrect)
      setWrongTargetId(targetId)
    }
    setSelectedTermId(null)
  }

  useEffect(() => {
    if (!isComplete || isFinished) return
    const hasUnseenPairs = seenIdsRef.current.size < poolPairs.length
    const timeoutId = window.setTimeout(() => {
      if (!hasUnseenPairs) {
        setIsFinished(true)
        return
      }

      const nextPairs = pickUnseenPairs(poolPairs, roundSize, seenIdsRef.current)
      if (nextPairs.length === 0) {
        setIsFinished(true)
        return
      }

      nextPairs.forEach((pair) => seenIdsRef.current.add(pair.id))
      applyRound(nextPairs)
    }, fadeDelayMs + fadeDurationMs + 200)

    return () => window.clearTimeout(timeoutId)
  }, [fadeDelayMs, fadeDurationMs, isComplete, isFinished, poolPairs, roundSize])

  const handleDragStart = (termId: string, event: DragEvent<HTMLButtonElement>) => {
    if (matchedTermIds.has(termId)) return
    event.dataTransfer.setData('text/plain', termId)
    event.dataTransfer.effectAllowed = 'move'
    setDraggingTermId(termId)
  }

  const handleDragEnd = () => {
    setDraggingTermId(null)
    setActiveTargetId(null)
  }

  const handleDrop = (targetId: string, event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    const termId = event.dataTransfer.getData('text/plain')
    if (!termId) return
    attemptMatch(termId, targetId)
    setDraggingTermId(null)
    setActiveTargetId(null)
  }

  const handleTargetClick = (targetId: string) => {
    if (!selectedTermId) return
    attemptMatch(selectedTermId, targetId)
  }

  const handleTermClick = (termId: string) => {
    if (matchedTermIds.has(termId)) return
    setSelectedTermId((current) => (current === termId ? null : termId))
  }

  const progressLabel = formatTemplate(config.progressAriaLabel, {
    matched: matchedCount,
    total: totalPairs,
  })

  const themeToggleLabel = activeTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'

  const getStatusBar = () => (
    <div className="ddm-status-bar">
      {config.showStatCard ? (
        <div className="ddm-score">
          <span>{matchedCount}</span> / <span>{totalPairs}</span> {config.scoreLabel}
          <span className="ddm-score-total-size">{config.pairs.length} cards in deck</span>
        </div>
      ) : (
        <div />
      )}
      <div className="ddm-status-actions">
        {config.showStatCard ? (
          <cer-btn type="button" className="ddm-button" data-style="ghost" onClick={startNewRound}>
            {config.shuffleLabel}
          </cer-btn>
        ) : null}
        <cer-btn
          type="button"
          className="ddm-icon-button"
          data-style="ghost"
          onClick={() => setHostTheme(activeTheme === 'dark' ? 'light' : 'dark')}
          title={themeToggleLabel}
          aria-label={themeToggleLabel}
        >
          <i
            className={
              activeTheme === 'dark' ? 'fa-duotone fa-sun-bright fa-fw' : 'fa-duotone fa-moon-stars fa-fw'
            }
          />
        </cer-btn>
        <button type="button" className="ddm-close" aria-label="Close matching game" onClick={() => setIsOpen(false)}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
    </div>
  )

  return (
    <>
      {!isOpen && (
        <div className="ddm-launch font-study" data-theme={activeTheme}>
          <div className="ddm-launch-content">
            <img className="ddm-launch-icon" src={puzzleIcon} alt="" aria-hidden="true" />
            <div className="ddm-launch-text">
              <div className="ddm-launch-title">Ready to play?</div>
              <div className="ddm-launch-subtitle">
                Match all the terms with their definitions as fast as you can.
              </div>
            </div>
          </div>
          <cer-btn type="button" className="ddm-start-button" data-variant="info" onClick={() => setIsOpen(true)}>
            Start
          </cer-btn>
        </div>
      )}

      {isOpen && (
        <div
          className="ddm-modal-overlay"
          data-theme={activeTheme}
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setIsOpen(false)
            }
          }}
        >
          <div className="ddm-modal font-study" role="dialog" aria-modal="true" aria-label="Matching game">
            <div className="ddm-modal-progress" role="img" aria-label={progressLabel}>
              <span style={{ width: `${progress * 100}%` }} />
            </div>
            <div className="ddm-modal-bar">{getStatusBar()}</div>
            <div className={`ddm-game${isFinished ? ' is-finished' : ''}`} data-theme={activeTheme}>
              {isFinished ? (
                <div className="ddm-finish">
                  <p className="ddm-finish-title">Finished! Want to play again?</p>
                  <cer-btn type="button" className="ddm-button" data-variant="info" onClick={startFresh}>
                    Play Again
                  </cer-btn>
                </div>
              ) : (
                <>
                  <section className="ddm-board">
                    <div className="ddm-panel ddm-panel--terms">
                      <h2>{config.termsTitle}</h2>
                      <div className="ddm-list">
                        {terms.filter((pair) => !hiddenTermIds.has(pair.id)).map((pair, index) => {
                          const isMatched = matchedTermIds.has(pair.id)
                          const isSelected = selectedTermId === pair.id
                          const isDragging = draggingTermId === pair.id
                          const isFading = fadingTermIds.has(pair.id)
                          const delayStyle = { '--cer-delay': `${index * 70}ms` } as CSSProperties

                          return (
                            <button
                              key={pair.id}
                              type="button"
                              className={`ddm-card${isMatched ? ' is-matched' : ''}${isSelected ? ' is-selected' : ''}${
                                isDragging ? ' is-dragging' : ''
                              }${isFading ? ' is-fading' : ''}`}
                              draggable={!isMatched}
                              onDragStart={(event) => handleDragStart(pair.id, event)}
                              onDragEnd={handleDragEnd}
                              onClick={() => handleTermClick(pair.id)}
                              aria-pressed={isSelected}
                              style={delayStyle}
                            >
                              <span className="ddm-term">{pair.term}</span>
                              {isMatched ? (
                                <span className="ddm-chip ddm-chip--locked">{config.chipMatchedLabel}</span>
                              ) : isSelected ? (
                                <span className="ddm-chip">{config.chipSelectedLabel}</span>
                              ) : (
                                <span className="ddm-chip ddm-chip--ghost">{config.chipDragLabel}</span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    <div className="ddm-panel ddm-panel--targets">
                      <h2>{config.definitionsTitle}</h2>
                      <div className="ddm-list">
                        {targets.filter((pair) => !hiddenTargetIds.has(pair.id)).map((pair, index) => {
                          const matchedId = matches[pair.id]
                          const isMatched = Boolean(matchedId)
                          const isActive = activeTargetId === pair.id
                          const isWrong = wrongTargetId === pair.id
                          const matchedTerm = matchedId ? pairLookup.get(matchedId)?.term : null
                          const isFading = fadingTargetIds.has(pair.id)
                          const delayStyle = { '--cer-delay': `${index * 70}ms` } as CSSProperties

                          return (
                            <button
                              key={pair.id}
                              type="button"
                              className={`ddm-target${isMatched ? ' is-matched' : ''}${isActive ? ' is-active' : ''}${
                                isWrong ? ' is-wrong' : ''
                              }${isFading ? ' is-fading' : ''}`}
                              onDragOver={(event) => event.preventDefault()}
                              onDragEnter={() => setActiveTargetId(pair.id)}
                              onDragLeave={() => setActiveTargetId(null)}
                              onDrop={(event) => handleDrop(pair.id, event)}
                              onClick={() => handleTargetClick(pair.id)}
                              disabled={isMatched}
                              style={delayStyle}
                            >
                              <span className="ddm-definition">{pair.match}</span>
                              <span className="ddm-match-label">
                                {isMatched
                                  ? matchedTerm
                                  : selectedTermId
                                    ? config.targetTapLabel
                                    : config.targetDropLabel}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </section>

                  <div className="ddm-feedback" aria-live="polite">
                    { feedback }
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
